
import React, { useEffect, useState } from "react";

// reactstrap components
import {
    UncontrolledAlert,
    Button,
    Card,
    CardHeader,
    Table,
    Container,
    Row,
    Col,
    UncontrolledTooltip,
    Modal,
    CardBody,
    Form,
    FormGroup,
    Input,
    ModalBody,
    ModalHeader,
    Label,
    Pagination, PaginationItem, PaginationLink,
} from "reactstrap";

// core components
import SimpleHeader from "components/Headers/SimpleHeader.js";
import { getAllInvoices, deleteInvoice, updateInvoice, sendPaymentLink, sendReceipt } from "api/einvoiceApiCall";
import { useNavigate } from 'react-router-dom';
import InvoiceReceipt from "./InvoiceReceipt";
import ReactDOMServer from 'react-dom/server';
import Swal from 'sweetalert2'
import { Loader } from "lucide-react";
import { getUserLogo } from "api/authApiCall";



function Einvoice() {
    const [allInvoices, setAllInvoices] = useState([]);
    const [filteredInvoices, setFilteredInvoices] = useState([]);
    const [allInvoicesError, setAllInvoicesError] = useState({});
    const [formModal, setFormModal] = React.useState(false);
    const [paymentFormModal, setPaymentFormModal] = React.useState(false);
    const [paymentReceiptModal, setPaymentReceiptModal] = React.useState(false);
    const [showSuccessAlert, setShowSuccessAlert] = useState(false);
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [errorMessages, setErrorMessages] = useState([]);
    const [successMessages, setSuccessMessages] = useState([]);
    const [selectedInvoice, setSelectedInvoice] = useState({});
    const [tooltipIds, setTooltipIds] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (Array.isArray(allInvoices)) {
            const ids = allInvoices.map((_, index) => ({
                edit: `tooltipEdit${index}`,
                delete: `tooltipDelete${index}`,
                print: `tooltipPrint${index}`,
                payment: `tooltipPayment${index}`,
                receipt: `tooltipReceipt${index}`,
            }));
            setTooltipIds(ids);
        } else {
            console.warn('allInvoices is not an array:', allInvoices);
        }
    }, [allInvoices]);

    useEffect(() => {
        const results = allInvoices.filter(invoice =>
            invoice.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredInvoices(results);
        setCurrentPage(1);
    }, [searchTerm, allInvoices]);

    const toggleModal = () => {
        setFormModal(!formModal);
    };

    const paymentToggleModal = () => {
        setPaymentFormModal(!paymentFormModal);
    };

    const receiptToggleModal = () => {
        setPaymentReceiptModal(!paymentReceiptModal);
    };

    const handleEditClick = (invoiceData) => {
        setSelectedInvoice(invoiceData);
        toggleModal();
    };

    const handlePaymentLink = (invoiceData) => {
        setSelectedInvoice(invoiceData);
        paymentToggleModal();
    };

    const handleReceipt = (invoiceData) => {
        setSelectedInvoice(invoiceData);
        receiptToggleModal();
    };

    const calculateSubtotal = (products) => {
        return products.reduce((total, product) => {
            return total + (parseFloat(product.price) || 0) * (parseFloat(product.quantity) || 0);
        }, 0) - (parseFloat(selectedInvoice.discount) || 0);
    };

    useEffect(() => {
        if (selectedInvoice.invoice_date) {
            const formattedDate = formatDate(selectedInvoice.invoice_date);
            setSelectedInvoice(prev => ({ ...prev, invoice_date: formattedDate }));
        }
    }, [selectedInvoice._id]);

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toISOString().split('T')[0];
    };

    const handleDateChange = (e) => {
        setSelectedInvoice({ ...selectedInvoice, invoice_date: e.target.value });
    };

    const handleProductChange = (index, field, value) => {
        const updatedProducts = [...selectedInvoice.products];
        updatedProducts[index] = { ...updatedProducts[index], [field]: value };
        setSelectedInvoice({ ...selectedInvoice, products: updatedProducts, subtotal: calculateSubtotal(updatedProducts) });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const [errorInvoicesCall, successUserCall] = await updateInvoice(selectedInvoice);
            if (errorInvoicesCall) {
                setErrorMessages(errorInvoicesCall.message);
                setShowErrorAlert(true);
            } else {

                setShowSuccessAlert(true);
                window.location.reload();
            }
        } catch (error) {
            setErrorMessages([error.message]);
            setShowErrorAlert(true);
        }
    };

    const handleDeleteClick = (invoiceId) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deleteInvoice(invoiceId)
                    .then(([error, successData]) => {
                        if (error) {
                            Swal.fire(
                                'Error!',
                                error.message || 'Failed to delete invoice. Please try again.',
                                'error'
                            );
                        } else {
                            Swal.fire(
                                'Deleted!',
                                'Your invoice has been deleted.',
                                'success'
                            ).then(() => {
                                window.location.reload();
                            });
                        }
                    })
                    .catch(error => {
                        Swal.fire(
                            'Error!',
                            'An unexpected error occurred. Please try again.',
                            'error'
                        );
                    });
            }
        });
    };





    const handlePaymentSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const [errorPaymentLink, successPaymentLink] = await sendPaymentLink(selectedInvoice);

            if (errorPaymentLink) {
                setErrorMessages([errorPaymentLink.message]);
                setShowErrorAlert(true);
            } else {
                setSuccessMessages([successPaymentLink.message]);
                setShowSuccessAlert(true);
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        } catch (error) {
            setErrorMessages([error.message]);
            setShowErrorAlert(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleReceiptSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            const [errorPaymentReceipt, successPaymentReceipt] = await sendReceipt(selectedInvoice);
            if (errorPaymentReceipt) {
                setErrorMessages([errorPaymentReceipt.message]);
                setShowErrorAlert(true);
            } else {
                setShowSuccessAlert(true);
                setTimeout(() => {
                    window.location.reload();
                }, 2000);
            }
        } catch (error) {
            setErrorMessages([error.message]);
            setShowErrorAlert(true);
        } finally {
            setIsLoading(false);
        }
    };

    const handleInvoicesCall = async () => {
        const [errorInvoicesCall, successInvoiceCall] = await getAllInvoices();
        if (errorInvoicesCall) {
            setAllInvoicesError(errorInvoicesCall);
        } else if (successInvoiceCall) {
            setAllInvoices(successInvoiceCall);
        } else {
            setAllInvoicesError({ message: 'No data received from the server' });
        }
    };

    useEffect(() => {
        handleInvoicesCall();
    }, [])

    useEffect(() => {
        const timeOutError = setTimeout(() => {
            setAllInvoicesError({});
        }, 10000)
        return () => {
            clearTimeout(timeOutError);
        }
    }, [allInvoicesError])

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = filteredInvoices.slice(indexOfFirstItem, indexOfLastItem);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);


    //Print
    const handlePrintInvoice = (invoiceData) => {
        const logoUrl = getUserLogo();
        // console.log(localStorage.getItem('userSettings'));
        // Ensure this contains the correct data
        // console.log("Logo URL:", logoUrl);

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <html>
                <head>
                    <title>aloPay</title>
                     <link rel="icon" href="/public/White (1).png" type="image/x-icon">
                    <style>
                       @media print {
                        @page {
                            margin: 0;
                        }
                        body {
                            margin: 1.6cm;
                        }
                        /* Hide the URL */
                        @page :first {
                            margin-top: 0;
                        }
                    }
                       .invoice-container {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: __Inter_d65c78,__Inter_Fallback_d65c78
}

.invoice-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 30px;
}

.company-name {
  color: #4d67fd;
  font-size: 1.5em;
  margin: 0;
}

.invoice-info {
  text-align: right;
}

.address-container {
  display: flex;
  justify-content: space-between;
  margin-bottom: 30px;
}

.invoice-items {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 30px;
}

.invoice-items th, .invoice-items td {
  border: 1px solid #ddd;
  padding: 10px;
  text-align: left;
}

.invoice-items th {
  background-color: #f2f2f2;
}

.invoice-summary {
  width: 300px;
  margin-left: auto;
}

.summary-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
}

.total {
  font-weight: bold;
  border-top: 1px solid #ddd;
  padding-top: 10px;
}

.thank-you {
  margin-top: 30px;
  color: #888;
}

footer {
  margin-top: 50px;
  text-align: center;
  color: #888;
}
                    </style>
                </head>
                <body>
                    ${ReactDOMServer.renderToString(<InvoiceReceipt invoice={invoiceData} logoUrl={logoUrl} />)}
                </body>
            </html>
        `);
        printWindow.document.close();



        // Wait for the document to load and styles to apply before triggering print
        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.onafterprint = () => printWindow.close();
        };
    };

    return (
        <>
            <SimpleHeader name="Invoices" parentName="Invoices" />
            <Container className="mt--6" fluid>
                <Card>
                    <CardHeader className="border-0">
                        <Row>
                            <Col xs="6">
                                <h3 className="mb-0">Invoices Management</h3>
                            </Col>
                            <Col className="text-right" xs="6">
                                <Button
                                    className="btn-round btn-icon"
                                    color="primary"
                                    id="tooltip443412080"
                                    onClick={() => navigate('/admin/addeinvoices')}
                                    size="sm"
                                >
                                    <span className="btn-inner--icon mr-1">
                                        <i className="fas fa-user-edit" />
                                    </span>
                                    <span className="btn-inner--text">Add Invoice</span>
                                </Button>
                                <UncontrolledTooltip delay={0} target="tooltip443412080">
                                    Add invoice
                                </UncontrolledTooltip>
                            </Col>
                        </Row>
                    </CardHeader>
                    <div className="px-3 py-2">
                        <Input
                            type="text"
                            placeholder="Search invoices..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                        />
                    </div>

                    {allInvoices && allInvoices.length > 0 && (
                        <Table className="align-items-center table-flush" responsive striped>
                            <thead className="thead-light">
                                <tr>
                                    <th>Customer name</th>
                                    <th>Customer Address</th>
                                    <th>Customer Email</th>
                                    <th>Invoice Date</th>
                                    <th>Product Name</th>
                                    <th>Quantity</th>
                                    <th>Price</th>
                                    <th>Discount</th>
                                    <th>Subtotal</th>
                                    <th>Status</th>
                                    <th>Action</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.map((invoiceData, index) => (

                                    <tr key={index}>
                                        <td>{invoiceData?.customer_name}</td>
                                        <td>{invoiceData?.customer_address}</td>
                                        <td>{invoiceData?.customer_email}</td>
                                        <td>{new Date(invoiceData?.invoice_date).toLocaleDateString()}</td>

                                        <td>
                                            {invoiceData?.products?.map((product, prodIndex) => (
                                                <div key={prodIndex}>{product.product_name}</div>
                                            ))}
                                        </td>
                                        <td>
                                            {invoiceData?.products?.map((product, prodIndex) => (
                                                <div key={prodIndex}>{product.quantity}</div>
                                            ))}
                                        </td>
                                        <td>
                                            {invoiceData?.products?.map((product, prodIndex) => (
                                                <div key={prodIndex}>{product.price}</div>
                                            ))}
                                        </td>
                                        <td>{invoiceData?.discount}</td>
                                        <td>{invoiceData?.subtotal}</td>
                                        <td>{invoiceData?.paymentStatus ? "Paid" : "Not paid"}</td>
                                        <td className="table-actions">
                                            <a
                                                className="table-action"
                                                href="#pablo"
                                                id={tooltipIds[index]?.edit}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleEditClick(invoiceData);
                                                }}
                                            >
                                                <i className="fas fa-user-edit" />
                                            </a>
                                            {tooltipIds[index] && (
                                                <UncontrolledTooltip delay={0} target={tooltipIds[index].edit}>
                                                    Edit invoice
                                                </UncontrolledTooltip>
                                            )}
                                            <a
                                                className="table-action"
                                                id={tooltipIds[index]?.print}
                                                onClick={() => handlePrintInvoice(invoiceData)}
                                            >
                                                <i className="fas fa-print" />
                                            </a>
                                            {tooltipIds[index] && (
                                                <UncontrolledTooltip delay={0} target={tooltipIds[index].print}>
                                                    Print Invoice
                                                </UncontrolledTooltip>
                                            )}
                                            <a
                                                className="table-action table-action-delete"
                                                href="#pablo"
                                                id={tooltipIds[index]?.delete}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleDeleteClick(invoiceData._id);
                                                }}
                                            >
                                                <i className="fas fa-trash" />
                                            </a>
                                            {tooltipIds[index] && (
                                                <UncontrolledTooltip delay={0} target={tooltipIds[index].delete}>
                                                    Delete invoice
                                                </UncontrolledTooltip>
                                            )}
                                            <a
                                                className="table-action table-action-delete"
                                                href="#pablo"
                                                id={tooltipIds[index]?.payment}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handlePaymentLink(invoiceData);
                                                }}
                                            >
                                                <i className="fas fa-credit-card" />
                                            </a>
                                            {tooltipIds[index] && (
                                                <UncontrolledTooltip delay={0} target={tooltipIds[index].payment}>
                                                    Create payment link
                                                </UncontrolledTooltip>
                                            )}
                                            <a
                                                className="table-action table-action-delete"
                                                href="#pablo"
                                                id={tooltipIds[index]?.receipt}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleReceipt(invoiceData);
                                                }}
                                            >
                                                <i className="fas fa-receipt" />
                                            </a>
                                            {tooltipIds[index] && (
                                                <UncontrolledTooltip delay={0} target={tooltipIds[index].receipt}>
                                                    Send Receipt
                                                </UncontrolledTooltip>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )
                    }

                    <div className="card-footer py-4">
                        <Pagination>
                            <PaginationItem disabled={currentPage === 1}>
                                <PaginationLink previous onClick={() => paginate(currentPage - 1)} />
                            </PaginationItem>
                            {[...Array(Math.ceil(filteredInvoices.length / itemsPerPage))].map((_, index) => (
                                <PaginationItem key={index} active={index + 1 === currentPage}>
                                    <PaginationLink onClick={() => paginate(index + 1)}>
                                        {index + 1}
                                    </PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={currentPage === Math.ceil(filteredInvoices.length / itemsPerPage)}>
                                <PaginationLink next onClick={() => paginate(currentPage + 1)} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                </Card>
            </Container>

            <Modal className="modal-dialog-centered" size="sm" isOpen={formModal} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>Edit Invoice</ModalHeader>
                <ModalBody>
                    <Card className="bg-secondary border-0 mb-0">
                        <CardBody className="">
                            <div className="text-center text-muted mb-4">
                                <small>Edit invoice details</small>
                                {showSuccessAlert && (
                                    <UncontrolledAlert color="success">
                                        <span className="alert-text ml-1">
                                            <strong>Success!</strong> Invoice Updated
                                        </span>
                                    </UncontrolledAlert>
                                )}
                                {showErrorAlert && (
                                    <UncontrolledAlert color="danger">
                                        <span className="alert-text ml-1">
                                            <strong>Error!</strong> Invoice updation failed
                                        </span>
                                    </UncontrolledAlert>
                                )}
                            </div>
                            <Form onSubmit={handleSubmit}>
                                <Row>
                                    <Label className="form-control-label" md="4">Customer Name</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Customer name"
                                                type="text"
                                                value={selectedInvoice.customer_name || ''}
                                                onChange={(e) => setSelectedInvoice({ ...selectedInvoice, customer_name: e.target.value })}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Label className="form-control-label" md="4">Address</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Customer Address"
                                                type="text"
                                                value={selectedInvoice.customer_address || ''}
                                                onChange={(e) => setSelectedInvoice({ ...selectedInvoice, customer_address: e.target.value })}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Label className="form-control-label" md="4">Email</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Customer Email"
                                                type="text"
                                                value={selectedInvoice.customer_email || ''}
                                                onChange={(e) => setSelectedInvoice({ ...selectedInvoice, customer_email: e.target.value })}
                                                required
                                                readOnly
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Label className="form-control-label" md="4">Invoice Date</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Date"
                                                type="date"
                                                name="invoice-date"
                                                id="invoice-date"
                                                value={selectedInvoice.invoice_date || ''}
                                                onChange={handleDateChange}
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Label className="form-control-label" md="4">Discount</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Discount"
                                                type="text"
                                                value={selectedInvoice.discount || ''}
                                                onChange={(e) => setSelectedInvoice({ ...selectedInvoice, discount: e.target.value })}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                {selectedInvoice.products && selectedInvoice.products.map((product, index) => (
                                    <div key={index}>
                                        <Row>
                                            <Label className="form-control-label" md="4" for={`product-name-${index}`}>Product Name</Label>
                                            <Col md="8">
                                                <FormGroup>
                                                    <Input
                                                        type="text"
                                                        name={`product-name-${index}`}
                                                        id={`product-name-${index}`}
                                                        value={product.product_name || ''}
                                                        onChange={(e) => handleProductChange(index, 'product_name', e.target.value)}
                                                        required
                                                        readOnly
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Label className="form-control-label" md="4" for={`quantity-${index}`}>Quantity</Label>
                                            <Col md="8">
                                                <FormGroup>
                                                    <Input
                                                        placeholder="Quantity"
                                                        type="number"
                                                        name={`quantity-${index}`}
                                                        id={`quantity-${index}`}
                                                        value={product.quantity || ''}
                                                        onChange={(e) => handleProductChange(index, 'quantity', e.target.value)}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <Row>
                                            <Label className="form-control-label" md="4" for={`price-${index}`}>Price</Label>
                                            <Col md="8">
                                                <FormGroup>
                                                    <Input
                                                        placeholder="Price"
                                                        type="number"
                                                        name={`price-${index}`}
                                                        id={`price-${index}`}
                                                        value={product.price || ''}
                                                        onChange={(e) => handleProductChange(index, 'price', e.target.value)}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}


                                <Row>
                                    <Label className="form-control-label" md="4">Subtotal</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Subtotal"
                                                type="text"
                                                name="subtotal"
                                                id="subtotal"
                                                value={selectedInvoice.subtotal || ''}
                                                readOnly
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Button color="primary" type="submit">Submit</Button>
                                <Button color="secondary" onClick={toggleModal}>Cancel</Button>
                            </Form>
                        </CardBody>
                    </Card>
                </ModalBody>
            </Modal>

            {/* //Payment Link Modal */}
            <Modal className="modal-dialog-centered" size="sm" isOpen={paymentFormModal} toggle={paymentToggleModal}>
                <ModalHeader toggle={paymentToggleModal}>Send payment link</ModalHeader>
                <ModalBody>
                    <Card className="bg-secondary border-0 mb-0">
                        <CardBody className="">
                            <div className="text-center text-muted mb-4">
                                {showSuccessAlert && (
                                    <UncontrolledAlert color="success">

                                        <span className="alert-text ml-1">
                                            <strong>Success!</strong> Payment link sent
                                        </span>
                                    </UncontrolledAlert>
                                )}
                                {showErrorAlert && (
                                    <UncontrolledAlert color="danger">
                                        <span className="alert-text ml-1">
                                            <strong>Error!</strong> Payment link already sent
                                        </span>
                                    </UncontrolledAlert>
                                )}
                            </div>
                            <Form onSubmit={handlePaymentSubmit}>
                                <Row>
                                    <Label className="form-control-label" md="4">To</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Customer name"
                                                type="text"
                                                value={selectedInvoice.customer_name || ''}
                                                readOnly
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Label className="form-control-label" md="4">Email</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Customer Address"
                                                type="text"
                                                value={selectedInvoice.customer_email || ''}
                                                required
                                                readOnly
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Button color="primary" type="submit" disabled={isLoading}>
                                    {isLoading ? (
                                        <>
                                            <Loader className="animate-spin mr-2" size={16} />
                                            Submitting...
                                        </>
                                    ) : (
                                        'Submit'
                                    )}
                                </Button>
                                <Button color="secondary" onClick={paymentToggleModal} disabled={isLoading}>
                                    Cancel
                                </Button>
                                {/* <Button color="primary" type="submit">Submit</Button>
                                <Button color="secondary" onClick={paymentToggleModal}>Cancel</Button> */}
                            </Form>
                        </CardBody>
                    </Card>
                </ModalBody>
            </Modal>


            <Modal className="modal-dialog-centered" size="sm" isOpen={paymentReceiptModal} toggle={receiptToggleModal}>
                <ModalHeader toggle={receiptToggleModal}>Send Receipt</ModalHeader>
                <ModalBody>
                    <Card className="bg-secondary border-0 mb-0">
                        <CardBody className="">
                            <div className="text-center text-muted mb-4">
                                {showSuccessAlert && (
                                    <UncontrolledAlert color="success">

                                        <span className="alert-text ml-1">
                                            <strong>Success!</strong>Receipt sent
                                        </span>
                                    </UncontrolledAlert>
                                )}
                                {showErrorAlert && (
                                    <UncontrolledAlert color="danger">
                                        <span className="alert-text ml-1">
                                            <strong>Error!</strong> wrong email or email sent already
                                        </span>
                                    </UncontrolledAlert>
                                )}
                            </div>
                            <Form onSubmit={handleReceiptSubmit}>
                                <Row>
                                    <Label className="form-control-label" md="4">To</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Customer name"
                                                type="text"
                                                value={selectedInvoice.customer_name || ''}
                                                readOnly
                                                required
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <Row>
                                    <Label className="form-control-label" md="4">Email</Label>
                                    <Col md="8">
                                        <FormGroup>
                                            <Input
                                                placeholder="Customer Address"
                                                type="text"
                                                value={selectedInvoice.customer_email || ''}
                                                required
                                                readOnly
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>
                                <div className="text-center">
                                    <Button color="primary" type="submit" disabled={isLoading}>
                                        {isLoading ? (
                                            <>
                                                <Loader className="animate-spin mr-2" size={16} />
                                                Submitting...
                                            </>
                                        ) : (
                                            'Submit'
                                        )}
                                    </Button>
                                    <Button color="secondary" onClick={receiptToggleModal} disabled={isLoading}>
                                        Cancel
                                    </Button>
                                </div>
                            </Form>
                        </CardBody>
                    </Card>
                </ModalBody>
            </Modal>
        </>
    );
}

export default Einvoice;
